import React, { ReactElement } from 'react';
import {
	Button,
	Checkbox,
	Flex,
	FlexItem,
	Form,
	FormGroup,
	Modal,
	ModalVariant,
	NumberInput,
	Select,
	SelectDirection,
	SelectOption,
	SelectOptionObject,
	SelectVariant,
	TextInput,
} from '@patternfly/react-core';
import { TPeriod } from '../../../api/analytics/Period';
import { DraggableMenuItemData } from '../../../types/databuilder/databuilder';
import { useMount } from 'react-use';
import { TUnitType } from '../../../api/analytics/UnitType';

export type IFactOptionsModalProps = {
	periods: TPeriod[];
	fact: DraggableMenuItemData | null;
	unitType?: TUnitType;
	handleClose: () => void;
	handleSave: (fact: DraggableMenuItemData) => void;
};

const FactOptionsModal = (props: IFactOptionsModalProps): ReactElement => {
	const [isDecimalDropdownOpen, setIsDecimalDropdownOpen] = React.useState(false);
	const [isDefaultDropdownOpen, setIsDefaultDropdownOpen] = React.useState(false);
	const [isPeriodDropdownOpen, setIsPeriodDropdownOpen] = React.useState(false);
	const [title, setTitle] = React.useState(props.fact ? props.fact?.data?.title : '');
	const [minimumValue, setMinimumValue] = React.useState(
		props.fact ? props.fact?.data?.minValue ?? undefined : undefined
	);
	const [maximumValue, setMaximumValue] = React.useState(
		props.fact ? props.fact?.data?.maxValue ?? undefined : undefined
	);
	const [maximumValueEmptyChecked, setMaximumValueEmptyChecked] = React.useState(false);
	const [minimumValueEmptyChecked, setMinimumValueEmptyChecked] = React.useState(false);
	const [hidden, setHidden] = React.useState(false);
	const [selectedDecimal, setSelectedDecimal] = React.useState<string | SelectOptionObject>(
		props.fact && props.fact.data?.numDecimals !== undefined
			? props.fact.data?.numDecimals.toString()
			: props.unitType && props.unitType.precision
			? props.unitType.precision.toString()
			: ''
	);
	const [selectedDefault, setSelectedDefault] = React.useState<string | SelectOptionObject>(
		props.fact && props.fact.data?.defaultWhenNull ? props.fact.data?.defaultWhenNull : '***'
	);
	const [selectedPeriod, setSelectedPeriod] = React.useState<string | SelectOptionObject>(
		props.fact && props.fact.data?.period ? props.fact.data?.period.toString() : ''
	);

	useMount(() => {
		if (props.unitType?.code === 'PCT') {
			if (props.fact?.data?.minValue) {
				setMinimumValue(props.fact?.data?.minValue * 100);
			}

			if (props.fact?.data?.maxValue) {
				setMaximumValue(props.fact?.data?.maxValue * 100);
			}
		}
	});

	const onCloseModal = () => {
		props.handleClose();
	};

	const getMinMaxForSave = (value: number | undefined) => {
		if (!value) {
			return;
		}

		if (props.unitType?.code === 'PCT') {
			return value / 100;
		}

		return value;
	};

	const onSaveModal = () => {
		const fact = Object.assign({}, props.fact);
		if (fact.data) {
			fact.data.defaultWhenNull = selectedDefault.toString();
			fact.data.numDecimals = parseInt(selectedDecimal.toString());
			fact.data.hidden = hidden;
			fact.data.title = title ?? '';
			fact.data.minValue = getMinMaxForSave(minimumValue);
			fact.data.maxValue = getMinMaxForSave(maximumValue);

			if (selectedPeriod) {
				fact.data.period = parseInt(selectedPeriod.toString());
			}
		}

		props.handleSave(fact);
	};

	const onToggleDecimalDropdown = (isOpen: boolean) => {
		setIsDecimalDropdownOpen(isOpen);
	};

	const onToggleDefaultDropdown = (isOpen: boolean) => {
		setIsDefaultDropdownOpen(isOpen);
	};

	const onTogglePeriodDropdown = (isOpen: boolean) => {
		setIsPeriodDropdownOpen(isOpen);
	};

	const onSelectDecimalDropdown = (
		event: React.MouseEvent<Element, MouseEvent> | React.ChangeEvent<Element>,
		selection: string | SelectOptionObject,
		isPlaceholder?: boolean | undefined
	) => {
		if (!isPlaceholder) {
			setSelectedDecimal(selection);
			setIsDecimalDropdownOpen(false);
		}
	};

	const onSelectDefaultDropdown = (
		event: React.MouseEvent<Element, MouseEvent> | React.ChangeEvent<Element>,
		selection: string | SelectOptionObject,
		isPlaceholder?: boolean | undefined
	) => {
		if (!isPlaceholder) {
			setSelectedDefault(selection);
			setIsDefaultDropdownOpen(false);
		}
	};

	const onSelectPeriodDropdown = (
		event: React.MouseEvent<Element, MouseEvent> | React.ChangeEvent<Element>,
		selection: string | SelectOptionObject,
		isPlaceholder?: boolean | undefined
	) => {
		if (!isPlaceholder) {
			setSelectedPeriod(selection);
			setIsPeriodDropdownOpen(false);
		}
	};

	const onTitleChange = (title: string) => {
		setTitle(title);
	};

	const getDecimalOptions = (): JSX.Element[] => {
		return [
			<SelectOption
				value={'0'}
				key={'decimal_0'}
			>
				0 {props.unitType?.precision === 0 ? '(Default)' : ''}
			</SelectOption>,
			<SelectOption
				value={'1'}
				key={'decimal_1'}
			>
				0.0 {props.unitType?.precision === 1 ? '(Default)' : ''}
			</SelectOption>,
			<SelectOption
				value={'2'}
				key={'decimal_2'}
			>
				0.00{' '}
				{props.unitType?.precision === 2 || props.unitType?.precision === undefined
					? '(Default)'
					: ''}
			</SelectOption>,
			<SelectOption
				value={'3'}
				key={'decimal_3'}
			>
				0.000 {props.unitType?.precision === 3 ? '(Default)' : ''}
			</SelectOption>,
			<SelectOption
				value={'4'}
				key={'decimal_4'}
			>
				0.0000 {props.unitType?.precision === 4 ? '(Default)' : ''}
			</SelectOption>,
			<SelectOption
				value={'5'}
				key={'decimal_5'}
			>
				0.00000 {props.unitType?.precision === 5 ? '(Default)' : ''}
			</SelectOption>,
			<SelectOption
				value={'6'}
				key={'decimal_6'}
			>
				0.000000 {props.unitType?.precision === 6 ? '(Default)' : ''}
			</SelectOption>,
		];
	};

	const getDefaultOptions = (): JSX.Element[] => {
		return [
			<SelectOption
				value={'***'}
				key={'default_*'}
			>
				*** (Default)
			</SelectOption>,
			<SelectOption
				value={'0'}
				key={'default_0'}
			>
				0
			</SelectOption>,
			<SelectOption
				value={'N/A'}
				key={'default_na'}
			>
				N/A
			</SelectOption>,
		];
	};

	const getPeriodOptions = (): JSX.Element[] => {
		const periods = [
			<SelectOption
				key={'select_a_period_placeholder'}
				value="Select a period"
				isPlaceholder
			/>,
		];

		return [
			...periods,
			...props.periods.map((type): ReactElement => {
				return (
					<SelectOption
						value={type.id.toString()}
						key={type.code}
					>
						{type.name}
					</SelectOption>
				);
			}),
		];
	};

	const onMinValMinus = () => {
		const newValue = (minimumValue || 0) - 1;
		setMinimumValue(newValue);
	};

	const onMinValChange = (event: React.FormEvent<HTMLInputElement>) => {
		const value = (event.target as HTMLInputElement).value;
		setMinimumValue(value === '' ? undefined : +value);
	};

	const onMinValPlus = () => {
		const newValue = (minimumValue || 0) + 1;
		setMinimumValue(newValue);
	};

	const onMaxValMinus = () => {
		const newValue = (maximumValue || 0) - 1;
		setMaximumValue(newValue);
	};

	const onMaxValChange = (event: React.FormEvent<HTMLInputElement>) => {
		const value = (event.target as HTMLInputElement).value;
		setMaximumValue(value === '' ? undefined : +value);
	};

	const onMaxValPlus = () => {
		const newValue = (maximumValue || 0) + 1;
		setMaximumValue(newValue);
	};

	const handleMinValNull = (checked: boolean, event: React.FormEvent<HTMLInputElement>) => {
		setMinimumValue(undefined);
		setMinimumValueEmptyChecked(checked);
	};

	const handleMaxValNull = (checked: boolean, event: React.FormEvent<HTMLInputElement>) => {
		setMaximumValue(undefined);
		setMaximumValueEmptyChecked(checked);
	};

	return (
		<React.Fragment>
			<Modal
				title="Options"
				variant={ModalVariant.small}
				isOpen={true}
				onClose={onCloseModal}
				actions={[
					<Button
						key="confirm"
						variant="primary"
						onClick={onSaveModal}
					>
						Ok
					</Button>,
					<Button
						key="cancel"
						variant="secondary"
						onClick={onCloseModal}
					>
						Cancel
					</Button>,
				]}
			>
				<Form>
					<FormGroup
						label="Decimal Precision"
						fieldId="decimal_precision"
					>
						<Select
							variant={SelectVariant.single}
							onToggle={onToggleDecimalDropdown}
							onSelect={onSelectDecimalDropdown}
							selections={selectedDecimal}
							isOpen={isDecimalDropdownOpen}
							isDisabled={false}
							direction={SelectDirection.down}
							menuAppendTo={() => document.body}
						>
							{getDecimalOptions()}
						</Select>
					</FormGroup>
					<FormGroup
						label="Default value when NULL"
						fieldId="default_when_null"
					>
						<Select
							variant={SelectVariant.single}
							onToggle={onToggleDefaultDropdown}
							onSelect={onSelectDefaultDropdown}
							selections={selectedDefault}
							isOpen={isDefaultDropdownOpen}
							isDisabled={false}
							direction={SelectDirection.down}
							menuAppendTo={() => document.body}
						>
							{getDefaultOptions()}
						</Select>
					</FormGroup>
					<FormGroup
						label="Title"
						fieldId="title"
					>
						<TextInput
							type="text"
							id="title"
							name="title"
							value={title}
							onChange={onTitleChange}
						/>
					</FormGroup>
					<FormGroup
						label="Minimum Value"
						fieldId="minimum_value"
					>
						<Flex>
							<FlexItem>
								<NumberInput
									value={minimumValue}
									onMinus={onMinValMinus}
									onChange={onMinValChange}
									onPlus={onMinValPlus}
									inputName="input"
									inputAriaLabel="number input"
									minusBtnAriaLabel="minus"
									plusBtnAriaLabel="plus"
									allowEmptyInput={true}
									isDisabled={minimumValueEmptyChecked}
								/>
							</FlexItem>
							<FlexItem>
								<Checkbox
									label="Reset to default"
									isChecked={minimumValueEmptyChecked}
									onChange={handleMinValNull}
									id="controlled-check-1"
									name="check1"
								/>
							</FlexItem>
						</Flex>
					</FormGroup>
					<FormGroup
						label="Maximum Value"
						fieldId="maximum_value"
					>
						<Flex>
							<FlexItem>
								<NumberInput
									value={maximumValue}
									onMinus={onMaxValMinus}
									onChange={onMaxValChange}
									onPlus={onMaxValPlus}
									inputName="input"
									inputAriaLabel="number input"
									minusBtnAriaLabel="minus"
									plusBtnAriaLabel="plus"
									allowEmptyInput={true}
									isDisabled={maximumValueEmptyChecked}
								/>
							</FlexItem>
							<FlexItem>
								<Checkbox
									label="Reset to default"
									isChecked={maximumValueEmptyChecked}
									onChange={handleMaxValNull}
									id="controlled-check-2"
									name="check2"
								/>
							</FlexItem>
						</Flex>
					</FormGroup>
					{/* <FormGroup isInline>
						<Checkbox
							label="Hidden"
							id="hidden"
							isChecked={hidden}
							onChange={setHidden}
						/>
					</FormGroup>
					<FormGroup
						label="Display Date Period"
						fieldId="period"
					>
						<Select
							variant={SelectVariant.single}
							onToggle={onTogglePeriodDropdown}
							onSelect={onSelectPeriodDropdown}
							selections={selectedPeriod}
							isOpen={isPeriodDropdownOpen}
							isDisabled={false}
							direction={SelectDirection.down}
							menuAppendTo={() => document.body}
						>
							{getPeriodOptions()}
						</Select>
					</FormGroup> */}
				</Form>
			</Modal>
		</React.Fragment>
	);
};

export default FactOptionsModal;
